/**
 * PATH: packages/ui/src/kanban/workbook/prefetchKanbanWorkbookIntent.ts
 * FILE_ID: UI-KANBAN-WORKBOOK-PREFETCH-001
 * PURPOSE: Warm workbook modal + AG Grid (+ optional per-card query) on intent.
 * WHY: Cold open was serial modal→GET→AG Grid (~10s). Intent-only warm overlaps
 *   those costs without touching Kanban board cold open.
 * FLOW: hover/focus Calculator → warm chunks; optional prefetchQuery for card.
 * RELATED: KanbanCardHoverMenu; OpenWorkbookButton; CardWorkbookSection.
 */
import type { QueryClient } from '@tanstack/react-query'

import { warmSheetGridAg } from '@quotech/quote-builder/grid'

import type { CardDocumentPayload } from '../types'

export function kanbanWorkbookQueryKey(cardId: string) {
  return ['kanban', 'card-document', cardId, 'workbook'] as const
}

export type PrefetchKanbanWorkbookIntentInput = {
  /** When set with getWorkbook + queryClient, warms the per-card document cache. */
  cardId?: string
  getWorkbook?: (cardId: string) => Promise<CardDocumentPayload>
  queryClient?: QueryClient
}

/** Kick modal + AG Grid chunks (and optional workbook GET) after user intent. */
export function prefetchKanbanWorkbookIntent(
  input: PrefetchKanbanWorkbookIntentInput = {},
): void {
  void import('../components/KanbanCardWorkbookModal')
  void warmSheetGridAg()
  const { cardId, getWorkbook, queryClient } = input
  if (!cardId || !getWorkbook || !queryClient) return
  void queryClient.prefetchQuery({
    queryKey: kanbanWorkbookQueryKey(cardId),
    queryFn: () => getWorkbook(cardId),
    staleTime: 5 * 60_000,
  })
}
